// externals
import * as React from 'react';
import { connect } from 'react-redux';

// libraries
import { ITradeInState, navigateTo } from '@makemydeal/dr-activities-common';
import { onOpenLeadFormModal } from '@makemydeal/dr-activities-lead-form';
import { urlBuilder } from '@makemydeal/dr-common-utils';
import { Resources } from '@makemydeal/dr-activities-common';

// components
import TradeSummaryUI from './TradeSummaryUI';
import { default as TradeSummaryUILegacy } from './TradeSummaryUI.old';

// interfaces/types
import { ITradeSummaryUIDispatchProps, ITradeSummaryUIProps, ITradeSummaryUIStateProps } from './TradeSummaryUI';
import { IRootProps } from '../../../../components/RootProps';

// selectors
import * as tradeInSelectors from '../../../../store/mmd/tradeIn';
import * as globalConfigFeatureToggleSelectors from '../../../../store/globalConfigFeatureToggles';

// actions
import * as tradeInComponentActionCreators from '../../../../store/actionCreators';

// consts/enums
import { Actions as types } from '../../../../store/actions';

// utils
import { isLeadFormSubmittedSuccess } from '../../../../store/mmd/leadForm';
import { getNextCardDetails, getNextStepInfo } from '../../../../store/mmd/navigationCard';
import { processStepperRoutes } from '../../../../utils/tradeInDecisionLogic';
import { STEPPER_ROUTES } from '../../../../utils';
import * as tradeInRoutes from '../../../../utils/routes';

// config
import config from '../../../../store/mmd/config';
import { isDealerUser, isDeskingUser, isShowroomExperience } from '../../../../utils/configUtils';

import * as tradeInActionCreators from '../../../../store/actionCreators';
import * as dealerSelectors from '../../../../store/mmd/dealerSelectors';

const mapStateToProps = (
    state: any,
    ownProps: IRootProps
): ITradeSummaryUIStateProps & { isSubaruGTPSharedComponentsEnabled?: boolean } => {
    const { tradeIn, tradeInState } = tradeInSelectors.getTradeInState(state, ownProps.tradeIndex);
    const cardDetails = getNextCardDetails(state);
    const shouldIncludeTradeIn = tradeInSelectors.getShouldIncludeTradeIn(tradeIn);
    
    const imageFPO = urlBuilder.buildFromConfig(config.staticImages, Resources.FPO_IMAGE);
    const isLeadFormSubmitted = isLeadFormSubmittedSuccess(state);
    const isAmdLienPayoffQuoteSpOnSPEnabled = tradeInSelectors.isAmdLienPayoffQuoteEnabled(state);
    const isSubaruGtpEligible = tradeInSelectors.isSubaruGtpEligible(tradeInState);
    const tradeInSubaruGtpFlowError = tradeInSelectors.getTradeInSubaruGtpFlowError(tradeInState);
    const isSubaruGTPSharedComponentsEnabled = tradeInSelectors.isSubaruGTPSharedComponentsEnabled(state);
    const vehicleOptions = tradeInSelectors.getTradeInVehicleOptions(tradeIn);
    const isMultiTradeEnabled = tradeInSelectors.isMultiTradeInsEnabled(state);
    const tradePaymentFailed = tradeInSelectors.getTradePaymentFailed(tradeInState);
    const vehicleId = tradeInSelectors.getTradeInVehicleId(tradeIn);
    const zipCode = tradeInSelectors.getTradeInZip(tradeIn);
    const vehicleMileage = tradeInSelectors.getTradeInVehicleMileage(tradeIn);
    const vehicleCondition = tradeInSelectors.getTradeInVehicleCondition(tradeIn);
    const year = tradeInSelectors.getTradeInVehicleYear(tradeIn);
    const make = tradeInSelectors.getTradeInVehicleMakeName(tradeIn);
    const model = tradeInSelectors.getTradeInVehicleModelName(tradeIn);
    const vin = tradeInSelectors.getTradeInVehicleVin(tradeIn);
    const tradeInValue = tradeInSelectors.getTradeInValue(tradeIn);
    const loanBalance = tradeInSelectors.getTradeInAmountOwed(tradeIn);
    const calculatedTradeInValue = tradeInValue - loanBalance;
    const currentDate = new Date();
    const expirationDate = tradeInSelectors.getExpirationDate(tradeIn);
    const userIsDealer = isDealerUser(ownProps.config);
    const userIsDesking = isDeskingUser(ownProps.config);
    const dealerPhoneNumber = dealerSelectors.getDealerPhone(state);
    const isLeadFormEnabled = tradeInSelectors.shouldShowLeadForm(state);
    const isManualEntryFlow = tradeInSelectors.isManualEntryFlow(state);
    const valueSource = tradeInSelectors.getValueSource(tradeIn);
    const isSalesView = isShowroomExperience(ownProps.config);
    const stepperRoutes = processStepperRoutes(
        state,
        STEPPER_ROUTES.summaryICO,
        isSalesView,
        isAmdLienPayoffQuoteSpOnSPEnabled,
        ownProps.tradeIndex
    );
    const ghostVinDealerConfig = dealerSelectors.isGhostVinDealerEnabled(state);
    const isGhostVinEnabled = globalConfigFeatureToggleSelectors.isGhostVinEnabled();
    const nextStepInfo = getNextStepInfo(state);
    const showActionButton = nextStepInfo ? true : false;
    const actionButtonText = nextStepInfo ? nextStepInfo.title : '';
    const selectedLoanBalanceOption = tradeInSelectors.getTradeInSelectedLoanBalanceOption(tradeIn);
    const hasOpenLoan = tradeIn?.hasOpenLoan ?? null;

    return {
        cardDetails,
        isTradeLeadFormEnabled: true,
        imageFPO,
        isSubaruGtpEligible,
        isLeadFormSubmitted,
        vehicleOptions,
        tradePaymentFailed,
        vehicleId,
        zipCode,
        vehicleMileage,
        vehicleCondition,
        year,
        make,
        model,
        vin,
        tradeInValue,
        loanBalance,
        calculatedTradeInValue,
        currentDate,
        expirationDate,
        userIsDealer,
        userIsDesking,
        dealerPhoneNumber,
        isLeadFormEnabled,
        isManualEntryFlow,
        valueSource,
        stepperRoutes,
        ghostVinDealerConfig,
        isGhostVinEnabled,
        actionButtonText,
        showActionButton,
        isAmdLienPayoffQuoteSpOnSPEnabled,
        selectedLoanBalanceOption,
        isMultiTradeEnabled,
        shouldIncludeTradeIn,
        hasOpenLoan,
        tradeInSubaruGtpFlowError,
        isSubaruGTPSharedComponentsEnabled,
        tradeIndex: ownProps.tradeIndex,
        urlPath: ownProps.tradeInRoute
    };
};

const mapDispatchToProps = (dispatch: any, ownProps: IRootProps): ITradeSummaryUIDispatchProps => {
    return {
        goToRoute: (route) => {
            dispatch(tradeInComponentActionCreators.updateTradeInCurrentLocation(route));
        },
        editTradeIn: () => {
            dispatch(tradeInComponentActionCreators.trackEditIconOnCompletionClicked());
        },
        termsDetailsClicked: () => {
            dispatch(tradeInComponentActionCreators.trackTermsDetailsOnCompletionClicked());
        },
        loadLeadForm: (skipLink) => {
            dispatch(
                onOpenLeadFormModal(
                    'Almost to Your Trade-In Value',
                    "Your trade-in value is on the next page.  Let's submit your deal status to unlock.",
                    skipLink,
                    types.TRADE_IN_LEAD_SUBMITTED,
                    types.TRADE_IN_PERFORM_UPDATE,
                    types.REMOVE_TRADE_IN_AND_CLOSE_MODAL
                )
            );
        },
        addRemoveTradeToDeal: (shouldIncludeTradeIn) => {
            if (shouldIncludeTradeIn) {
                dispatch(tradeInComponentActionCreators.removeTradeToDeal());
            } else {
                dispatch(tradeInComponentActionCreators.addTradeToDeal());
            }
        },
        dispatchAnalytics: (type, description) => {
            dispatch(tradeInComponentActionCreators.dispatchAnalytics(type, description));
        },
        tradeInEstimateDisplayed: () => {
            dispatch(tradeInComponentActionCreators.sendTradeInEstimateDisplayedAnalytics());
        },
        removeTrade: () => {
            dispatch(tradeInComponentActionCreators.updateTradeInCurrentLocation(tradeInRoutes.TRADE_IN));
            dispatch(tradeInActionCreators.removeTradeIn());
        },
        goToNextStep: () => {
            dispatch((dispatch, getState) => {
                const state = getState();
                const nextStepInfo = getNextStepInfo(state);
                if (nextStepInfo) {
                    dispatch(tradeInComponentActionCreators.tradeInCompletedContinue());
                    dispatch(tradeInComponentActionCreators.continueToNextActivityPress(nextStepInfo.route));
                    dispatch(navigateTo(nextStepInfo.route));
                }
            });
        },
        cdlBackButtonClicked: () => {
            dispatch(tradeInComponentActionCreators.cdlBackButtonClicked('Summary'));
        }
    };
};

type ITradeSummaryWrapperProps = ITradeSummaryUIProps & { isSubaruGTPSharedComponentsEnabled?: boolean };

const TradeSummaryWrapper: React.FC<ITradeSummaryWrapperProps> = ({ isSubaruGTPSharedComponentsEnabled, ...props }) =>
    isSubaruGTPSharedComponentsEnabled ? <TradeSummaryUI {...props} /> : <TradeSummaryUILegacy {...props} />;

const TradeSummary = connect(mapStateToProps, mapDispatchToProps)(TradeSummaryWrapper);

export default TradeSummary;
